import type {
  ProfileCookieSettings,
  ProfileDemographics,
  ProfileEmploymentDto,
  ProfileLocation,
} from '@/api/core-api/types.gen';

import { LeadStatus, profilesControllerFindByUserId } from '@/api/core-api';
import { getClient } from '@/utils/get-api-client';

export type Profile = {
  id: string;
  isOnboardingComplete: boolean;
  isPendingLead: boolean;
  /**
   * Whether the participant has ever completed a LinkedIn connection.
   *
   * `socialLinks.linkedin.id` is only ever written by the VoL OAuth flow, so
   * its presence means they finished one (a hand-entered profile URL sets
   * `.url` alone). Deliberately NOT `employment.isLinkedInVerified`: that flag
   * tracks whether their *employment* was corroborated by LinkedIn and is
   * falsy for ~21.5k members who have connected, so keying the connect-LinkedIn
   * prompt off it re-prompts people who already did the thing.
   */
  hasConnectedLinkedIn: boolean;
  demographics?: ProfileDemographics;
  location?: ProfileLocation;
  employment?: ProfileEmploymentDto;
  cookieSettings?: ProfileCookieSettings;
  codeOfConductAcceptedAt: string | null;
};

/**
 * Server function to get the profile for a user.
 * Accepts userId directly instead of fetching session.
 */
export const getProfile = async (userId: string): Promise<Profile | null> => {
  if (!userId) return null;

  try {
    const response = await profilesControllerFindByUserId({
      client: getClient(userId),
      path: {
        userId,
      },
    });

    if (response.error || !response.data) {
      console.error('Failed to fetch profile:', response.status);
      return null;
    }

    const gender = response.data.demographics?.gender;
    const dateOfBirth = response.data.demographics?.dateOfBirth;
    const location = response.data.location?.city;

    const isOnboardingComplete = !!(gender && dateOfBirth && location);

    return {
      id: response.data.id,
      isOnboardingComplete,
      hasConnectedLinkedIn:
        !!response.data.socialLinks?.linkedin?.id ||
        response.data.employment?.isLinkedInVerified === true,
      demographics: response.data.demographics,
      location: response.data.location,
      employment: response.data.employment ?? undefined,
      cookieSettings: response.data.cookieSettings,
      codeOfConductAcceptedAt: response.data.codeOfConductAcceptedAt ?? null,
      isPendingLead: response.data.leadStatus === LeadStatus.PENDING,
    };
  } catch (error) {
    console.error('Failed to fetch profile:', error);
    return null;
  }
};
